import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import React, { useEffect, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useUIStore } from './stores/uiStore';
import { useAuthStore } from './stores/authStore';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './layouts/DashboardLayout';
import AuthLayout from './layouts/AuthLayout';
import LoginPage from './pages/LoginPage';
import ForgotPassword from './pages/ForgotPassword';
import { ToastContainer } from './components/ui/Toast';
import SuperAdminProtectedRoute from './components/SuperAdminProtectedRoute';
import SuperAdminLayout from './layouts/SuperAdminLayout';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,     // 2 minutes before refetch
      gcTime: 10 * 60 * 1000,       // 10 minutes cache retention
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

/* ── Lazy-loaded page components ── */
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const StudentList = React.lazy(() => import('./pages/students/StudentList'));
const StudentForm = React.lazy(() => import('./pages/students/StudentForm'));
const StudentProfile = React.lazy(() => import('./pages/students/StudentProfile'));
const StudentFinancial = React.lazy(() => import('./pages/students/StudentFinancial'));
const ImportStudents = React.lazy(() => import('./pages/students/ImportStudents'));
const FeeSchedule = React.lazy(() => import('./pages/fees/FeeSchedule'));
const RecordPayment = React.lazy(() => import('./pages/fees/RecordPayment'));
const OverdueList = React.lazy(() => import('./pages/fees/OverdueList'));
const EmployeeList = React.lazy(() => import('./pages/hr/EmployeeList'));
const EmployeeForm = React.lazy(() => import('./pages/hr/EmployeeForm'));
const EmployeeProfile = React.lazy(() => import('./pages/hr/EmployeeProfile'));
const PayrollPeriods = React.lazy(() => import('./pages/payroll/PayrollPeriods'));
const PayrollDetail = React.lazy(() => import('./pages/payroll/PayrollDetail'));
const LeaveRequests = React.lazy(() => import('./pages/leave/LeaveRequests'));
const LeaveBalances = React.lazy(() => import('./pages/leave/LeaveBalances'));
const Absences = React.lazy(() => import('./pages/leave/Absences'));
const AdvanceList = React.lazy(() => import('./pages/hr/AdvanceList'));
const EmployeeSalaries = React.lazy(() => import('./pages/hr/EmployeeSalaries'));
const NotificationList = React.lazy(() => import('./pages/notifications/NotificationList'));
const ReminderSettings = React.lazy(() => import('./pages/settings/ReminderSettings'));
const SchoolProfile = React.lazy(() => import('./pages/settings/SchoolProfile'));
const SettingsHub = React.lazy(() => import('./pages/settings/SettingsHub'));
const AcademicYears = React.lazy(() => import('./pages/settings/AcademicYears'));
const CourseManagement = React.lazy(() => import('./pages/settings/CourseManagement'));
const SystemSettings = React.lazy(() => import('./pages/settings/SystemSettings'));
const GradingScales = React.lazy(() => import('./pages/settings/GradingScales'));
const AuditLog = React.lazy(() => import('./pages/settings/AuditLog'));
const EmployeeTypeManagement = React.lazy(() => import('./pages/settings/EmployeeTypeManagement'));
const TermManagement = React.lazy(() => import('./pages/settings/TermManagement'));
const DepartmentSettings = React.lazy(() => import('./pages/settings/DepartmentSettings'));
const UserManagement = React.lazy(() => import('./pages/settings/UserManagement'));
const AttendancePage = React.lazy(() => import('./pages/students/AttendancePage'));
const StudentPromotion = React.lazy(() => import('./pages/students/StudentPromotion'));
const StudentGrades = React.lazy(() => import('./pages/students/StudentGrades'));
const CourseGradeEntry = React.lazy(() => import('./pages/students/CourseGradeEntry'));
const ReportsHub = React.lazy(() => import('./pages/reports/ReportsHub'));
const IncomeStatement = React.lazy(() => import('./pages/reports/IncomeStatement'));
const BalanceSheet = React.lazy(() => import('./pages/reports/BalanceSheet'));
const CashFlow = React.lazy(() => import('./pages/reports/CashFlow'));
const VATReport = React.lazy(() => import('./pages/reports/VATReport'));
const DailyCollectionReport = React.lazy(() => import('./pages/reports/DailyCollectionReport'));
const OverdueStatement = React.lazy(() => import('./pages/reports/OverdueStatement'));
const StudentRoster = React.lazy(() => import('./pages/reports/StudentRoster'));
const PayrollSummaryReport = React.lazy(() => import('./pages/reports/PayrollSummary'));
const AttendanceReport = React.lazy(() => import('./pages/reports/AttendanceReport'));
const CafeteriaItems = React.lazy(() => import('./pages/cafeteria/CafeteriaItems'));
const CafeteriaSales = React.lazy(() => import('./pages/cafeteria/CafeteriaSales'));
const CafeteriaPurchases = React.lazy(() => import('./pages/cafeteria/CafeteriaPurchases'));
const CafeteriaSummary = React.lazy(() => import('./pages/cafeteria/CafeteriaSummary'));
const ExpenseList = React.lazy(() => import('./pages/expenses/ExpenseList'));
const BudgetManagement = React.lazy(() => import('./pages/expenses/BudgetManagement'));
const InvoiceList = React.lazy(() => import('./pages/fees/InvoiceList'));
const InvoicePage = React.lazy(() => import('./pages/fees/InvoicePage'));
const InvoiceSettings = React.lazy(() => import('./pages/settings/InvoiceSettings'));
const SuperAdminLogin = React.lazy(() => import('./pages/super-admin/SuperAdminLogin'));
const SuperAdminDashboard = React.lazy(() => import('./pages/super-admin/SuperAdminDashboard'));
const TenantList = React.lazy(() => import('./pages/super-admin/TenantList'));
const CreateTenant = React.lazy(() => import('./pages/super-admin/CreateTenant'));
const TenantDetail = React.lazy(() => import('./pages/super-admin/TenantDetail'));
const SuperAdminSettings = React.lazy(() => import('./pages/super-admin/SuperAdminSettings'));
const BillingDashboard = React.lazy(() => import('./pages/super-admin/BillingDashboard'));
const LogViewer = React.lazy(() => import('./pages/super-admin/LogViewer'));
const AlertsDashboard = React.lazy(() => import('./pages/super-admin/AlertsDashboard'));
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const ReportComments = React.lazy(() => import('./pages/academics/ReportComments'));
const SmartFeeReminders = React.lazy(() => import('./pages/academics/SmartFeeReminders'));
const StudentRiskAnalysis = React.lazy(() => import('./pages/academics/StudentRiskAnalysis'));
const ParentMessageComposer = React.lazy(() => import('./pages/academics/ParentMessageComposer'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const OnboardingChecklist = React.lazy(() => import('./pages/OnboardingChecklist'));

/* ── Loading spinner for Suspense fallback ── */
function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

export default function App() {
  const { i18n } = useTranslation();
  const direction = useUIStore((s) => s.direction);
  const language = useUIStore((s) => s.language);
  const checkAuth = useAuthStore((s) => s.checkAuth);

  useEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = language;
  }, [direction, language]);

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language, i18n]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <QueryClientProvider client={queryClient}>
    <div dir={direction} className="min-h-screen bg-gray-50 font-sans">
      <ToastContainer />
      <BrowserRouter>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Auth routes */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
            </Route>

            {/* Protected dashboard routes */}
            <Route
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/onboarding" element={<OnboardingChecklist />} />
              <Route path="/students" element={<StudentList />} />
              <Route path="/students/new" element={<StudentForm />} />
              <Route path="/students/:id" element={<StudentProfile />} />
              <Route path="/students/:id/edit" element={<StudentForm />} />
              <Route path="/students/import" element={<ImportStudents />} />
              <Route path="/students/:id/financial" element={<StudentFinancial />} />
              <Route path="/fees" element={<FeeSchedule />} />
              <Route path="/fees/payment" element={<RecordPayment />} />
              <Route path="/fees/overdue" element={<OverdueList />} />
              <Route path="/invoices" element={<InvoiceList />} />
              <Route path="/invoices/:id" element={<InvoicePage />} />
              <Route path="/hr/employees" element={<EmployeeList />} />
              <Route path="/hr/employees/new" element={<EmployeeForm />} />
              <Route path="/hr/employees/:id" element={<EmployeeProfile />} />
              <Route path="/hr/employees/:id/edit" element={<EmployeeForm />} />
              <Route path="/payroll" element={<PayrollPeriods />} />
              <Route path="/payroll/:id" element={<PayrollDetail />} />
              <Route path="/hr/leave" element={<LeaveRequests />} />
              <Route path="/hr/leave/balances" element={<LeaveBalances />} />
              <Route path="/hr/absences" element={<Absences />} />
              <Route path="/hr/advances" element={<AdvanceList />} />
              <Route path="/hr/salaries" element={<EmployeeSalaries />} />
              <Route path="/notifications" element={<NotificationList />} />
              <Route path="/settings" element={<SettingsHub />} />
              <Route path="/settings/school" element={<SchoolProfile />} />
              <Route path="/settings/reminders" element={<ReminderSettings />} />

              <Route path="/settings/academic-years" element={<AcademicYears />} />
              <Route path="/settings/system" element={<SystemSettings />} />
              <Route path="/settings/grading-scales" element={<GradingScales />} />
              <Route path="/settings/audit-log" element={<AuditLog />} />
              <Route path="/settings/courses" element={<CourseManagement />} />
              <Route path="/settings/employee-types" element={<EmployeeTypeManagement />} />
              <Route path="/settings/terms" element={<TermManagement />} />
              <Route path="/settings/departments" element={<DepartmentSettings />} />
              <Route path="/settings/users" element={<UserManagement />} />
              <Route path="/settings/invoices" element={<InvoiceSettings />} />
              <Route path="/students/attendance" element={<AttendancePage />} />
              <Route path="/students/promotion" element={<StudentPromotion />} />
              <Route path="/students/:id/grades" element={<StudentGrades />} />
              <Route path="/students/grades" element={<CourseGradeEntry />} />
              <Route path="/academics/report-comments" element={<ReportComments />} />
              <Route path="/academics/fee-reminders" element={<SmartFeeReminders />} />
              <Route path="/academics/student-risk" element={<StudentRiskAnalysis />} />
              <Route path="/academics/parent-messages" element={<ParentMessageComposer />} />
              <Route path="/employees" element={<Navigate to="/hr/employees" replace />} />
              <Route path="/cafeteria/items" element={<CafeteriaItems />} />
              <Route path="/cafeteria/sales" element={<CafeteriaSales />} />
              <Route path="/cafeteria/purchases" element={<CafeteriaPurchases />} />
              <Route path="/cafeteria/summary" element={<CafeteriaSummary />} />
              <Route path="/expenses" element={<ExpenseList />} />
              <Route path="/budgets" element={<BudgetManagement />} />
              <Route path="/reports" element={<ReportsHub />} />
              <Route path="/reports/income-statement" element={<IncomeStatement />} />
              <Route path="/reports/balance-sheet" element={<BalanceSheet />} />
              <Route path="/reports/cash-flow" element={<CashFlow />} />
              <Route path="/reports/vat-report" element={<VATReport />} />
              <Route path="/reports/daily-collection" element={<DailyCollectionReport />} />
              <Route path="/reports/overdue-statement" element={<OverdueStatement />} />
              <Route path="/reports/student-roster" element={<StudentRoster />} />
              <Route path="/reports/payroll-summary" element={<PayrollSummaryReport />} />
              <Route path="/reports/attendance-report" element={<AttendanceReport />} />
            </Route>

            {/* Super admin routes — completely separate from tenant routes */}
            <Route path="/super-admin/login" element={<SuperAdminLogin />} />
            <Route
              element={
                <SuperAdminProtectedRoute>
                  <SuperAdminLayout />
                </SuperAdminProtectedRoute>
              }
            >
              <Route path="/super-admin/dashboard" element={<SuperAdminDashboard />} />
              <Route path="/super-admin/tenants" element={<TenantList />} />
              <Route path="/super-admin/tenants/new" element={<CreateTenant />} />
              <Route path="/super-admin/tenants/:id" element={<TenantDetail />} />
              <Route path="/super-admin/settings" element={<SuperAdminSettings />} />
              <Route path="/super-admin/billing" element={<BillingDashboard />} />
              <Route path="/super-admin/logs" element={<LogViewer />} />
              <Route path="/super-admin/alerts" element={<AlertsDashboard />} />
            </Route>

            {/* Public landing page */}
            <Route path="/" element={<LandingPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
    </QueryClientProvider>
  );
}
